import React, { useState, useContext } from "react";
import styled from "styled-components";
// React Router is not needed here since we show the Unauthorized page
// directly rather than redirecting.
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import StyledButton from "../components/StyledButton.jsx";
import WarningErrorSnackbars from "../components/WarningErrorSnackbar.jsx";
import SEO from "../components/SEO.jsx";
import { UserContext } from "../utils/context.js";
import Unauthorized from "./Unauthorized.jsx";
// Helper functions for retrieving user information
import { getUser } from "../utils/queries.js";
import { updateSecondsAvailableToTranscribe } from "../utils/mutations.js";
import { secsToString } from "../utils/helper functions/files_data_helpers.js";
import { API } from "aws-amplify";
import { createUsageRecord as createUsageRecordMutation } from "../graphql/mutations";

/**
 * AdminAddHoursPage
 *
 * A small admin-only interface for granting additional transcription
 * time to any user. The page verifies the current user is in the
 * "Admin" Cognito group and otherwise displays the Unauthorized page.
 *
 * Admins search for a user by ID, view their current time, and can
 * input hours/minutes to add. A preview shows the new total time
 * before applying the update and creating a UsageRecord for auditing.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  width: 300px;
`;

const FieldWrapper = styled.div`
  margin-top: 15px;
`;

const InfoText = styled.div`
  margin-top: 10px;
  font-size: 1rem;
`;

// Card displaying user details and time adjustment fields
const UserCard = styled(Card)`
  margin-top: 20px;
  width: 320px;
`;

// Wrapper for the before/after time preview with an arrow icon
const TimePreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryDark};
`;

const ArrowIcon = styled(ArrowForwardIosIcon)`
  margin: 0 8px;
`;

// -------------------------------------------------------------
// AdminAddHoursPage Component
// -------------------------------------------------------------
// Secure admin-only page for modifying a user's available
// transcription time. Admins look up a user by ID, specify the
// number of hours to grant, and provide an action description.
// Each adjustment is persisted via a UsageRecord for auditing.

function AdminAddHoursPage() {
  // Current signed in user (undefined while loading)
  const user = useContext(UserContext);
  // Text typed into the search box
  const [searchTerm, setSearchTerm] = useState("");
  // The user that matches the entered ID
  const [selectedUser, setSelectedUser] = useState(null);
  const [hoursToAdd, setHoursToAdd] = useState("");
  const [minutesToAdd, setMinutesToAdd] = useState("");
  const [actionDesc, setActionDesc] = useState(
    "Time purchased via one-time purchase"
  );
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [infoSnackbar, setInfoSnackbar] = useState(false);

  // Determine if the signed-in user belongs to the Admin group
  const groups =
    user?.signInUserSession?.accessToken?.payload["cognito:groups"] || [];
  const isAdmin = groups.includes("Admin");

  // If we cannot verify admin privileges, just show Unauthorized
  if (!isAdmin) {
    return <Unauthorized />;
  }

  // Look up the user by the provided ID and populate `selectedUser`
  const handleSearch = async () => {
    if (!searchTerm) return;
    try {
      const result = await getUser(searchTerm.trim());
      if (result && result.id) {
        setSelectedUser(result);
        setHoursToAdd("");
      } else {
        setSelectedUser(null);
        setErrorSnackbar("User not found");
      }
    } catch (err) {
      console.error(err);
      setSelectedUser(null);
      setErrorSnackbar("Error retrieving user");
    }
  };

  const handleAddTime = async () => {
    if (!selectedUser) return;
    const hrs = parseFloat(hoursToAdd) || 0;
    const mins = parseFloat(minutesToAdd) || 0;
    if (hrs < 0 || mins < 0 || (hrs === 0 && mins === 0)) {
      setErrorSnackbar("Please enter a valid amount of time");
      return;
    }
    const secondsToAdd = Math.round(hrs * 3600 + mins * 60);
    const newSeconds = selectedUser.secondsAvailableToTranscribe + secondsToAdd;
    try {
      await updateSecondsAvailableToTranscribe(selectedUser.id, newSeconds);
      await API.graphql({
        query: createUsageRecordMutation,
        variables: {
          input: {
            userID: selectedUser.id,
            date: new Date().toISOString(),
            action: actionDesc,
            duration: secondsToAdd,
            callID: null,
            availableTimeAfterTransaction: newSeconds,
          },
        },
      });
      setSelectedUser({
        ...selectedUser,
        secondsAvailableToTranscribe: newSeconds,
      });
      setHoursToAdd("");
      setMinutesToAdd("");
      setInfoSnackbar("Added time successfully");
    } catch (err) {
      console.error(err);
      setErrorSnackbar("Failed to add time");
    }
  };

  // Helper strings for displaying before/after values
  const currentTimeString = selectedUser
    ? secsToString(selectedUser.secondsAvailableToTranscribe, false, true)
    : "";

  const newTimeString =
    selectedUser && (hoursToAdd || minutesToAdd)
      ? secsToString(
          selectedUser.secondsAvailableToTranscribe +
            Math.round(
              parseFloat(hoursToAdd || 0) * 3600 +
                parseFloat(minutesToAdd || 0) * 60
            ),
          false,
          true
        )
      : "";

  return (
    <Wrapper>
      <SEO
        title="Admin Add Hours | WireTap"
        description="Add hours to a user"
      />
      <h1>Adjust User Time</h1>
      <FormWrapper>
        <FieldWrapper>
          <TextField
            fullWidth
            label="User ID"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </FieldWrapper>
        <FieldWrapper>
          <StyledButton color="white" onClick={handleSearch}>
            Search
          </StyledButton>
        </FieldWrapper>
        {selectedUser && (
          <UserCard>
            <CardContent>
              <InfoText>User ID: {selectedUser.id}</InfoText>
              <InfoText>Current Time: {currentTimeString}</InfoText>
              <InfoText>
                Show Intro Tutorial:{" "}
                {selectedUser.showIntroTutorial ? "Yes" : "No"}
              </InfoText>
              <FieldWrapper>
                <TextField
                  fullWidth
                  label="Hours to Add"
                  variant="outlined"
                  value={hoursToAdd}
                  onChange={(e) =>
                    setHoursToAdd(e.target.value.replace(/\D/g, ""))
                  }
                />
              </FieldWrapper>
              <FieldWrapper>
                <TextField
                  fullWidth
                  label="Minutes to Add"
                  variant="outlined"
                  value={minutesToAdd}
                  onChange={(e) =>
                    setMinutesToAdd(e.target.value.replace(/\D/g, ""))
                  }
                />
              </FieldWrapper>
              <FieldWrapper>
                <TextField
                  fullWidth
                  label="Action Description"
                  variant="outlined"
                  value={actionDesc}
                  onChange={(e) => setActionDesc(e.target.value)}
                />
              </FieldWrapper>
              {(hoursToAdd || minutesToAdd) && (
                <TimePreview>
                  {currentTimeString}
                  <ArrowIcon />
                  {newTimeString}
                </TimePreview>
              )}
              <FieldWrapper>
                <StyledButton color="white" onClick={handleAddTime}>
                  Apply Update
                </StyledButton>
              </FieldWrapper>
            </CardContent>
          </UserCard>
        )}
      </FormWrapper>
      <WarningErrorSnackbars
        errorMessage={errorSnackbar}
        setErrorMessage={setErrorSnackbar}
        infoMessage={infoSnackbar}
        setInfoMessage={setInfoSnackbar}
      />
    </Wrapper>
  );
}

export default AdminAddHoursPage;
